import React, { useState, useMemo, useEffect, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import shypvLogo from '../../img/Shypv Logo SVG White.svg';
import shypvLogoDark from '../../img/SHYPV LOGO DARK.svg';

/* ─── Design tokens ─────────────────────────────────────────────────── */
const C = {
  bg: '#f1f5f9',
  surface: '#ffffff',
  border: '#e2e8f0',
  text: '#0f172a',
  textMuted: '#64748b',
  textLight: '#94a3b8',
  brand: '#00a6ed',
  brandDark: '#0086c0',
  brandLight: '#e0f5ff',
  codeBase: '#0d1b2a',
  codeText: '#cdd9e5',
  codeGutter: '#162535',
  green: '#059669',
  greenBg: '#ecfdf5',
  greenBorder: '#a7f3d0',
  blue: '#2563eb',
  blueBg: '#eff6ff',
  blueBorder: '#bfdbfe',
  amber: '#b45309',
  amberBg: '#fffbeb',
  amberBorder: '#fde68a',
  red: '#dc2626',
  redBg: '#fef2f2',
  redBorder: '#fecaca',
  purple: '#6d28d9',
  purpleBg: '#f5f3ff',
  purpleBorder: '#ddd6fe',
  navy: '#1e3a5f',
};

const METHOD = {
  GET:    { bg: C.blue,   text: '#fff' },
  POST:   { bg: C.green,  text: '#fff' },
  PUT:    { bg: C.amber,  text: '#fff' },
  DELETE: { bg: C.red,    text: '#fff' },
};

const ENVS = {
  staging:    { label: 'Staging',    url: 'https://staging-api.shypv.com', color: C.amber,  bg: C.amberBg,  border: C.amberBorder },
  production: { label: 'Production', url: 'https://api.shypv.com',         color: C.green,  bg: C.greenBg,  border: C.greenBorder },
};

const font = "'Poppins', sans-serif";
const mono = "'JetBrains Mono', 'Consolas', monospace";
const FONT_IMPORT = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap';

/* ─── Verified API sections (field names confirmed against backend source) */
const SECTIONS = [
  {
    id: 'authentication',
    title: 'Authentication',
    number: '01',
    method: 'POST',
    path: '/v2.0/order/create',
    needsAuth: false,
    tag: 'Auth',
    tagColor: C.purple,
    tagBg: C.purpleBg,
    tagBorder: C.purpleBorder,
    purpose: 'ShypV issues every approved partner TWO keys — an Access Key (public identifier) and a Secret Key. There is no separate login call: send BOTH on every request to the order API, as the "accessKey" and "token" request headers. Treat the Secret Key like a password and never expose it in client-side code.',
    note: 'Both keys appear in your ShypV partner profile once your KYC is approved. The Secret Key can be regenerated at any time — regenerating it invalidates the previous key immediately. The Access Key is optional for backward compatibility (secret-only requests still work), but sending both is recommended.',
    requestParams: [
      { name: 'accessKey', fullForm: 'Access Key — request header', type: 'String', desc: 'Public identifier for your partner account. Sent as the "accessKey" HTTP header.', req: true },
      { name: 'token',     fullForm: 'Secret Key — request header', type: 'String', desc: 'Your private secret key. Sent as the "token" HTTP header. Keep it confidential.', req: true },
    ],
    responseParams: [
      { name: 'success', fullForm: 'success', type: 'Boolean', desc: 'true when the key pair is valid and the partner is approved.' },
      { name: 'errors',  fullForm: 'errors',  type: 'Array',   desc: 'SC-195 "Invalid Api Key" if the key pair is wrong; SC-294 if the partner account is not yet approved.' },
    ],
    queryParams: [],
    bodySample: `# Send both keys as HTTP headers on every order-API request:

accessKey: 3fa85f6457174562b3fc2c963f66afa6
token:     ea4fe02dd16e42b3a530117a9736923a

# There is no /authenticate step — the header pair authenticates each call.`,
    responseSample: `{
  "data": null,
  "info": null,
  "errors": [],
  "success": true
}`,
    onResponse: null,
  },
  {
    id: 'booking',
    title: 'Create Booking',
    number: '02',
    method: 'POST',
    path: '/shypv/order/createOrder',
    needsAuth: true,
    tag: 'Orders',
    tagColor: C.green,
    tagBg: C.greenBg,
    tagBorder: C.greenBorder,
    purpose: 'Creates a new booking with pickup and delivery task details. The system assigns a booking reference ID on success. Both pickup and delivery task arrays must be supplied.',
    note: 'The payment mode defaults to "cash" if not provided. Set calculatePrice to false (handled automatically by the server).',
    validationNote: 'CONFIRMED FROM BACKEND SOURCE: The delivery task validation (GoOrderBusinessLogicImpl.java:534–541) checks: noitms must not be null; bkref must not be empty; jd must not be empty; dimwghts must be a non-empty array; dimwghts[0].wght must be a JSON number (not a string) and must be greater than 0; dimwghts[0].len and dimwghts[0].heght must not be null. Missing any of these returns errorCode 3 "Required fields are missing."',
    requestParams: [
      { name: 'prid',    fullForm: 'partner id',       type: 'Integer', desc: 'Your partner ID assigned by ShypV. Differs between staging and production.', req: true },
      { name: 'pkpcnt',  fullForm: 'pickup count',     type: 'Integer', desc: 'Number of pickup tasks.',                                                       req: true, def: '1' },
      { name: 'dlvcnt',  fullForm: 'delivery count',   type: 'Integer', desc: 'Number of delivery tasks.',                                                     req: true, def: '1' },
      { name: 'tamt',    fullForm: 'total amount',     type: 'Double',  desc: 'Total amount of the booking.',                                                   req: true, def: '0' },
      { name: 'ptyp',    fullForm: 'payment type',     type: 'String',  desc: '"payatpickup" or "delivery".',                                                   req: true },
      { name: 'cur',     fullForm: 'currency',         type: 'String',  desc: 'Currency code, e.g. "AED".',                                                    req: true },
      { name: 'vtyp',    fullForm: 'vehicle type',     type: 'String',  desc: '"Bike" or "Van".',                                                               req: true },
      { name: 'bktyp',   fullForm: 'booking type',     type: 'String',  desc: '"Next Day", "Same Day", or "On Demand".',                                       req: true },
      { name: 'dom',     fullForm: 'domain',           type: 'String',  desc: '"AE" for Dubai, "QA" for Qatar.',                                               req: true },
      { name: 'chnl',    fullForm: 'channel',          type: 'String',  desc: 'Channel — use "B2C".',                                                          req: true },
      { name: 'prcty',   fullForm: 'pricing city',     type: 'String',  desc: 'City used for price lookup, e.g. "Dubai".',                                     req: false },
      { name: 'manual',  fullForm: 'manual flag',      type: 'String',  desc: 'Pass "no".',                                                                    req: false },
      { name: 'pkp',     fullForm: 'pickup tasks',     type: 'Object[]',desc: 'Array of pickup task objects. See task object fields below.',                   req: true },
      { name: 'dlv',     fullForm: 'delivery tasks',   type: 'Object[]',desc: 'Array of delivery task objects. Same structure as pickup.',                     req: true },
    ],
    taskParams: [
      { name: 'add',    fullForm: 'address',            type: 'String',  desc: 'Full Google address string.',                       req: true },
      { name: 'lat',    fullForm: 'latitude',           type: 'Double',  desc: 'Latitude of the address.',                         req: true },
      { name: 'lon',    fullForm: 'longitude',          type: 'Double',  desc: 'Longitude of the address.',                        req: true },
      { name: 'tim',    fullForm: 'time',               type: 'String',  desc: 'Task datetime: "YYYY-MM-DD HH:mm:ss".',            req: true },
      { name: 'ph',     fullForm: 'phone',              type: 'String',  desc: 'Customer phone number (7–15 characters).',         req: true },
      { name: 'name',   fullForm: 'customer name',      type: 'String',  desc: 'Name of the customer.',                           req: true },
      { name: 'email',  fullForm: 'customer email',     type: 'String',  desc: 'Email of the customer.',                          req: true },
      { name: 'jd',     fullForm: 'job description',    type: 'String',  desc: 'Description of the job/package.',                 req: true },
      { name: 'addtl',  fullForm: 'additional address', type: 'String',  desc: 'Apartment, floor, or landmark details.',          req: true },
      { name: 'nationalAddress', fullForm: 'Saudi national address short code', type: 'String', desc: 'Saudi National Address short code for this stop — FOUR letters followed by FOUR digits, e.g. "RRRD2929". Send it only on a stop inside Saudi Arabia; leave the field out entirely for UAE and Qatar stops, which are unaffected. Spaces, hyphens and lower case are ignored ("rrrd 2929" and "RRRD-2929" are both stored as RRRD2929). Anything that is not 4 letters + 4 digits is rejected at create with SC-299 and no order is made. See the Saudi National Address notes below.', req: false },
      { name: 'ccamt',  fullForm: 'COD amount',         type: 'Double',  desc: 'Amount to collect from customer (0 if none).',    req: true, def: '0' },
      { name: 'isclct', fullForm: 'is collect',         type: 'Boolean', desc: 'True if COD amount should be collected.',         req: true, def: 'false' },
      { name: 'prc',    fullForm: 'price',              type: 'Double',  desc: 'Price of this individual task.',                  req: true, def: '0' },
      { name: 'bkref',  fullForm: 'booking reference',  type: 'String',  desc: 'Your internal reference for this task.',          req: false },
      { name: 'slot',   fullForm: 'time slot',          type: 'String',  desc: 'Delivery slot string, e.g. "9 AM - 6 PM".',       req: true },
      { name: 'cnam',   fullForm: 'city name',          type: 'String',  desc: 'City name, e.g. "Dubai".',                       req: false },
      { name: 'paytyp', fullForm: 'payment mode',       type: 'String',  desc: '"Cash", "Card", etc.',                           req: true },
      { name: 'imgs',   fullForm: 'images',             type: 'Array',   desc: 'Array of image URLs to attach.',                  req: false },
      { name: 'hbid',   fullForm: 'homebase id',  type: 'String', desc: 'The warehouse / depot this task is assigned to in the Locus dispatch system. Controls which physical hub processes the order. Must be provided together with teamid — both fields are required for correct routing. See the valid pairs table below.', req: true },
      { name: 'teamid', fullForm: 'team id',     type: 'String', desc: 'The dispatch team associated with the homebase in Locus. Routes the task to the correct operational team. Must always be paired with a matching hbid value. See the valid pairs table below.', req: true },
      { name: 'serviceType', fullForm: 'service type',  type: 'String',  desc: 'E.g. "Timexpress Next day Service".',            req: false },
      { name: 'dimwghts', fullForm: 'dimensional weights', type: 'Array', desc: 'Array of {len, widt, heght, wght} objects.',    req: false },
    ],
    responseParams: [
      { name: 'data.bkrefid', fullForm: 'booking reference id', type: 'String',  desc: 'Unique booking reference ID generated on success.' },
      { name: 'data.bkstat',  fullForm: 'booking status',       type: 'String',  desc: '"Created", "In Progress", or "Failed".' },
      { name: 'data.tamt',    fullForm: 'total amount',         type: 'Double',  desc: 'Confirmed total amount for the booking.' },
      { name: 'success',      fullForm: 'success',              type: 'Boolean', desc: 'Overall operation result.' },
    ],
    hbidTeamPairs: [
      { location: 'Dubai — Timexpress Warehouse (Production)', hbid: 'timexpress-warehouse', teamid: 'timexpress-team', env: 'Production' },
      { location: 'Dubai — Timexpress Warehouse (Staging)',    hbid: 'timexpress-warehouse', teamid: 'test_team_dubai',  env: 'Staging'    },
      { location: 'Dubai — Main Operations',                  hbid: 'Dubai',                teamid: 'dubai',            env: 'Both'       },
      { location: 'Dubai — Warehouse Operations (MPMD)',       hbid: 'Dubai_WareHouse',      teamid: 'dubaiteam',        env: 'Both'       },
      { location: 'Dubai — MPMD Operations',                  hbid: 'Dubai',                teamid: 'mpmd-dubai',       env: 'Both'       },
      { location: 'Abu Dhabi',                                hbid: 'Abu_Dhabi',            teamid: 'abu-dhabi',        env: 'Both'       },
      { location: 'Abu Dhabi — MPMD',                         hbid: 'Abu_Dhabi',            teamid: 'mpmd-abudhabi',    env: 'Both'       },
      { location: 'Doha, Qatar',                              hbid: 'Doha',                 teamid: 'doha',             env: 'Both'       },
      { location: 'Doha, Qatar — MPMD',                       hbid: 'Doha',                 teamid: 'doha-mpmd',        env: 'Both'       },
      { location: 'Fujairah',                                 hbid: 'Fujairah',             teamid: 'fujairah',         env: 'Both'       },
      { location: 'Timexpress (Careem Integration)',          hbid: 'timexpress',           teamid: 'te-warehouse',     env: 'Production' },
    ],
    nationalAddressNote: {
      title: 'Saudi National Address — nationalAddress (KSA stops only)',
      lines: [
        'Saudi Arabia has required a National Address short code on both parties of every shipment since January 2026, so the KSA last-mile carrier will not accept a shipment without one. Send it as "nationalAddress" on the pkp / dlv object for the stop that is inside Saudi Arabia.',
        'Format: FOUR letters followed by FOUR digits — for example RRRD2929. Spaces, hyphens and lower case are accepted and normalised away, so "rrrd 2929", "RRRD-2929" and " rrrd2929 " are all stored as RRRD2929.',
        'Optional everywhere else. It is required only for deliveries (or pickups) inside Saudi Arabia. UAE and Qatar orders should omit the field completely — an absent or blank value is never validated and never rejected, and those requests behave exactly as they always have. No other field changes either: dom stays "AE" or "QA", and currency and pricing are untouched.',
        'If the value is not 4 letters + 4 digits, the create call is rejected immediately with HTTP 400 and errorCode SC-299 ("National address must be 4 letters followed by 4 digits, e.g. RRRD2929."). Nothing is created — correct the code and re-post the same request.',
        'The shape check is not the whole story: the carrier resolves the code against the Saudi national address registry when the shipment is handed over, and answers "Cannot resolve national_address" for a code that does not exist there. A well-shaped but invented code therefore still fails the shipment days later, at the carrier, when you can no longer amend the request. RRRD2929 below shows the SHAPE only — always send the real short code issued for that address.',
        'On the v2.0 partner order API (POST /v2.0/order/create) the same value is the "national_address" property on each object inside the pickups[] and deliveries[] arrays (that API uses snake_case field names throughout). The format, the SC-299 rejection and the KSA-only rule are identical on both APIs.',
      ],
      requestSample: `"dlv": [
  {
    "bkref": "SA-10021",
    "name": "Customer Name",
    "add": "King Fahd Rd - Al Olaya - Riyadh - Saudi Arabia",
    "cnam": "Riyadh",
    "ph": "966500000000",
    "jd": "Documents",
    "tim": "2026-02-02 18:00:00",
    "addtl": "Building 4, Floor 2",
    "nationalAddress": "RRRD2929",
    "noitms": 1,
    "dimwghts": [{ "len": 1, "widt": 1, "heght": 1, "wght": 1 }]
  }
]`,
      errorSample: `{
  "data": null,
  "info": null,
  "errors": [
    {
      "errorCode": "SC-299",
      "errorMessage": "National address must be 4 letters followed by 4 digits, e.g. RRRD2929.",
      "errorDetails": "National address must be 4 letters followed by 4 digits, e.g. RRRD2929."
    }
  ],
  "success": false
}`,
    },
    queryParams: [],
    bodySample: `{
  "prcty": "Dubai",
  "prid": 52,
  "pkpcnt": 1,
  "dlvcnt": 1,
  "tamt": 11.55,
  "ptyp": "payatpickup",
  "cur": "AED",
  "vtyp": "Van",
  "bktyp": "Next Day",
  "bkpage": "dlvform",
  "chnl": "B2C",
  "dom": "AE",
  "manual": "no",
  "pkp": [
    {
      "add": "Al Garhoud - Dubai - United Arab Emirates",
      "lat": 25.2436289,
      "lon": 55.3474236,
      "tim": "2024-11-20 10:00:00",
      "ph": "0718928763",
      "jd": "",
      "imgs": [],
      "name": "Partner Name",
      "email": "partner@example.com",
      "prc": 0,
      "ccamt": 12,
      "isclct": true,
      "addtl": "al garhoud",
      "bkref": "",
      "slot": "12 PM - 2 PM",
      "hbid": "timexpress-warehouse",
      "teamid": "timexpress-team",
      "cnam": "Dubai",
      "paytyp": "Cash"
    }
  ],
  "dlv": [
    {
      "bkref": "abcde",
      "name": "Customer Name",
      "add": "41 31A St - Al Garhoud - Dubai - UAE",
      "cnam": "Dubai",
      "email": "customer@example.com",
      "ph": "971507254329",
      "jd": "Documents",
      "tim": "2024-11-21 18:00:00",
      "ccamt": 0,
      "isclct": false,
      "addtl": "2808",
      "lat": 25.243841980939987,
      "lon": 55.346782207489014,
      "slot": "9 AM - 6 PM",
      "isNextDay": true,
      "serviceType": "Timexpress Next day Service",
      "dimwghts": [{ "len": 0, "widt": 0, "heght": 0, "wght": "0.5" }]
    }
  ]
}`,
    responseSample: `{
  "data": {
    "bkrefid": "UB52-24B5533",
    "bkstat": "Created",
    "tamt": 94.5,
    "dplnk": "https://stage.shypv.com/go/book?trackMyOrder=true&id=..."
  },
  "info": null,
  "errors": [],
  "success": true
}`,
  },
  {
    id: 'cancellation',
    title: 'Cancel Booking',
    number: '03',
    method: 'POST',
    path: '/shypv/order/cancel',
    needsAuth: true,
    tag: 'Orders',
    tagColor: C.green,
    tagBg: C.greenBg,
    tagBorder: C.greenBorder,
    purpose: 'Cancels a task that is no longer required. The system marks the task as cancelled and updates status accordingly.',
    note: 'For a pickup task append _test to the booking ID. For a delivery task append _1_test (staging). Production IDs have no suffix.',
    requestParams: [
      { name: 'taskId', fullForm: 'task id', type: 'Query Param', desc: 'ID of the task to cancel. Example: UB52-24B5533_test (pickup) or UB52-24B5533_1_test (delivery).', req: true },
      { name: 'reason', fullForm: 'reason',  type: 'Query Param', desc: 'Reason for cancellation.', req: true },
    ],
    responseParams: [
      { name: 'data',    fullForm: 'data',    type: 'String',  desc: 'Confirmation message, e.g. "Task Cancelled Successfully".' },
      { name: 'success', fullForm: 'success', type: 'Boolean', desc: 'true on success, false on failure.' },
    ],
    queryParams: [
      { name: 'taskId', value: 'UB52-24B5533_test' },
      { name: 'reason', value: 'No longer required' },
    ],
    bodySample: '',
    responseSample: `{
  "data": "Task Cancelled Successfully",
  "info": null,
  "errors": [],
  "success": true
}`,
  },
  {
    id: 'amendment',
    title: 'Edit Booking',
    number: '04',
    method: 'POST',
    path: '/shypv/order/update',
    needsAuth: true,
    tag: 'Orders',
    tagColor: C.green,
    tagBg: C.greenBg,
    tagBorder: C.greenBorder,
    purpose: 'Amends an existing task — update the address, contact details, scheduled time or COD amount. All fields should be supplied even if unchanged.',
    note: 'The ordid field follows the same suffix convention as taskId in the cancellation endpoint.',
    requestParams: [
      { name: 'ordid',  fullForm: 'order id',         type: 'String', desc: 'Task ID to update: bookingid_test (pickup) or bookingid_1_test (delivery).', req: true },
      { name: 'add',    fullForm: 'address',           type: 'String', desc: 'Updated full Google address.',                                               req: true },
      { name: 'lat',    fullForm: 'latitude',          type: 'Double', desc: 'Updated latitude.',                                                         req: true },
      { name: 'lon',    fullForm: 'longitude',         type: 'Double', desc: 'Updated longitude.',                                                        req: true },
      { name: 'tim',    fullForm: 'time',              type: 'String', desc: 'Updated task time: "YYYY-MM-DD HH:mm:ss".',                                 req: true },
      { name: 'ph',     fullForm: 'phone',             type: 'String', desc: 'Updated customer phone.',                                                   req: true },
      { name: 'jd',     fullForm: 'job description',   type: 'String', desc: 'Updated job description.',                                                  req: true },
      { name: 'imgs',   fullForm: 'images',            type: 'Array',  desc: 'Images to attach.',                                                         req: true },
      { name: 'name',   fullForm: 'customer name',     type: 'String', desc: 'Updated customer name.',                                                    req: true },
      { name: 'email',  fullForm: 'customer email',    type: 'String', desc: 'Updated customer email.',                                                   req: true },
      { name: 'ccamt',  fullForm: 'COD amount',        type: 'Double', desc: 'Updated COD/collection amount.',                                            req: true },
      { name: 'dom',    fullForm: 'domain',            type: 'String', desc: '"AE" for Dubai, "QA" for Qatar.',                                           req: true },
      { name: 'jtyp',   fullForm: 'job type',          type: 'String', desc: '"Pickup" or "Delivery".',                                                   req: true },
    ],
    responseParams: [
      { name: 'data',    fullForm: 'data',    type: 'String',  desc: 'Confirmation message, e.g. "Task Updated Successfully".' },
      { name: 'success', fullForm: 'success', type: 'Boolean', desc: 'true on success, false on failure.' },
    ],
    queryParams: [],
    bodySample: `{
  "ordid": "UB52-24B5533_1_test",
  "add": "41 31A St , 2nd Floor - Al Garhoud - Dubai - UAE",
  "lat": 25.243841980939987,
  "lon": 55.346782207489014,
  "tim": "2024-11-26 18:00:00",
  "ph": "+971507254329",
  "jd": "Delivery",
  "imgs": [],
  "name": "Customer Name",
  "email": "customer@example.com",
  "ccamt": "",
  "dom": "AE",
  "jtyp": "Delivery"
}`,
    responseSample: `{
  "data": "Task Updated Successfully",
  "info": null,
  "errors": [],
  "success": true
}`,
  },
  {
    id: 'task-status',
    title: 'Task Status',
    number: '05',
    method: 'GET',
    path: '/shypv/order/task/status',
    needsAuth: true,
    tag: 'Tracking',
    tagColor: C.blue,
    tagBg: C.blueBg,
    tagBorder: C.blueBorder,
    purpose: 'Returns the current status of a task identified by its booking ID.',
    note: null,
    requestParams: [
      { name: 'bookingId', fullForm: 'booking id', type: 'Query Param', desc: 'Booking ID of the task, e.g. UB52-24B5533_1_test.', req: true },
    ],
    responseParams: [
      { name: 'data.stat', fullForm: 'status',  type: 'String',  desc: 'Current status code of the task. See status reference table below.' },
      { name: 'success',   fullForm: 'success', type: 'Boolean', desc: 'true on success, false on failure.' },
    ],
    queryParams: [{ name: 'bookingId', value: 'UB52-24B5533_1_test' }],
    bodySample: '',
    responseSample: `{
  "data": {
    "stat": "RECEIVED"
  },
  "info": null,
  "errors": [],
  "success": true
}`,
    statuses: [
      { code: 'RECEIVED',  color: C.textMuted, desc: 'Task created and awaiting assignment to an agent.' },
      { code: 'ASSIGNED',  color: C.blue,      desc: 'Agent has accepted the assigned task.' },
      { code: 'EXECUTING', color: C.amber,     desc: 'Task in progress — agent is en route.' },
      { code: 'COMPLETED', color: C.green,     desc: 'Task completed successfully.' },
      { code: 'CANCELLED', color: C.red,       desc: 'Task was cancelled.' },
    ],
  },
  {
    id: 'print-sticker',
    title: 'Print Sticker (AWB)',
    number: '06',
    method: 'GET',
    path: '/shypv/order/task/print/sticker',
    needsAuth: true,
    tag: 'Sticker',
    tagColor: C.amber,
    tagBg: C.amberBg,
    tagBorder: C.amberBorder,
    purpose: 'Returns a URL to download or print the Air Waybill (AWB) sticker for a specific task.',
    note: null,
    requestParams: [
      { name: 'bookingId', fullForm: 'booking id', type: 'Query Param', desc: 'Task booking ID, e.g. UB52-24B5533_1_test.', req: true },
    ],
    responseParams: [
      { name: 'data.url', fullForm: 'sticker url', type: 'String',  desc: 'Direct URL to open, download or print the AWB sticker.' },
      { name: 'success',  fullForm: 'success',      type: 'Boolean', desc: 'true on success, false on failure.' },
    ],
    queryParams: [{ name: 'bookingId', value: 'UB52-24B5533_1_test' }],
    bodySample: '',
    responseSample: `{
  "data": {
    "url": "https://business-agent-stage.shypv.com/print-order?id=UB52-24B5533_1_test"
  },
  "info": null,
  "errors": [],
  "success": true
}`,
  },
  {
    id: 'booking-details',
    title: 'Booking Details',
    number: '07',
    method: 'POST',
    path: '/shypv/order/bookinglist',
    needsAuth: true,
    tag: 'Reports',
    tagColor: C.purple,
    tagBg: C.purpleBg,
    tagBorder: C.purpleBorder,
    purpose: 'Retrieves a paginated list of bookings created within a specified date range. Maximum range is 7 days. Results are 10 records per page (0-indexed).',
    note: 'Dates must be supplied as strings in YYYY-MM-DD format. The prid field is your partner ID assigned by ShypV.',
    requestParams: [
      { name: 'prid',    fullForm: 'partner id',  type: 'Integer', desc: 'Your partner ID assigned by ShypV.',                req: true },
      { name: 'frmdate', fullForm: 'from date',   type: 'String',  desc: 'Start of the date range: "YYYY-MM-DD".',           req: true },
      { name: 'todate',  fullForm: 'to date',     type: 'String',  desc: 'End of the date range: "YYYY-MM-DD".',             req: true },
      { name: 'page',    fullForm: 'page number', type: 'Integer', desc: 'Page number, 0-indexed. 10 records per page.',     req: false, def: '0' },
    ],
    responseParams: [
      { name: 'ordid',   fullForm: 'Task ID',                type: 'String', desc: 'Task/Booking ID.' },
      { name: 'bkref',   fullForm: 'Booking Reference',      type: 'String', desc: 'Business reference number from partner.' },
      { name: 'jtyp',    fullForm: 'Job Type',               type: 'String', desc: '"Pickup" or "Delivery".' },
      { name: 'cname',   fullForm: 'Customer Name',          type: 'String', desc: 'Customer name on the task.' },
      { name: 'cemail',  fullForm: 'Customer Email',         type: 'String', desc: 'Customer email.' },
      { name: 'cadd',    fullForm: 'Customer Address',       type: 'String', desc: 'Customer address.' },
      { name: 'ptyp',    fullForm: 'Payment Type',           type: 'String', desc: 'cash, card, wallet, etc.' },
      { name: 'crdat',   fullForm: 'Creation Date',          type: 'String', desc: 'Booking creation timestamp (UTC).' },
      { name: 'tim',     fullForm: 'Job Time',               type: 'String', desc: 'Scheduled job time.' },
      { name: 'prc',     fullForm: 'Price',                  type: 'Float',  desc: 'Task charges.' },
      { name: 'ccamt',   fullForm: 'COD Amount',             type: 'Float',  desc: 'COD amount for the task.' },
      { name: 'stat',    fullForm: 'Task Status',            type: 'String', desc: 'Current status (text).' },
      { name: 'statnum', fullForm: 'Status (numeric)',       type: 'String', desc: 'Current status as a numeric code.' },
      { name: 'cmt',     fullForm: 'Collected Amount',       type: 'Float',  desc: 'Amount collected by driver on completion.' },
    ],
    queryParams: [],
    bodySample: `{
  "prid": 52,
  "frmdate": "2024-11-19",
  "todate": "2024-11-25",
  "page": 0
}`,
    responseSample: `{
  "data": [
    {
      "ordid": "UB52-24B5533_1_test",
      "jtyp": "Delivery",
      "cname": "Customer Name",
      "cemail": "customer@example.com",
      "cadd": "41 31A St , 2nd Floor - Al Garhoud - Dubai - UAE",
      "ptyp": "cash",
      "crdat": "2024-11-20 08:06:10",
      "tim": "2024-11-26 18:00:00",
      "stat": "EXECUTING",
      "statnum": "1"
    }
  ],
  "info": null,
  "errors": [],
  "success": true
}`,
  },
];

/* ─── Environment context ───────────────────────────────────────────── */
const EnvContext = createContext('staging');
const useEnv = () => {
  const key = useContext(EnvContext);
  return ENVS[key] || ENVS.staging;
};

/* ─── Shared helpers ─────────────────────────────────────────────────── */
function useCopy(text) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try { navigator.clipboard.writeText(text); } catch (_) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return [copied, copy];
}

/* ─── Atoms ─────────────────────────────────────────────────────────── */
function MethodPill({ method, small }) {
  const m = METHOD[method] || { bg: C.textMuted, text: '#fff' };
  return (
    <span style={{
      background: m.bg,
      color: m.text,
      fontFamily: mono,
      fontWeight: 600,
      fontSize: small ? 9 : 11,
      letterSpacing: 0.8,
      padding: small ? '3px 7px' : '4px 10px',
      borderRadius: 5,
      display: 'inline-block',
      textTransform: 'uppercase',
    }}>{method}</span>
  );
}

function Tag({ label, color, bg, border }) {
  return (
    <span style={{
      background: bg,
      color: color,
      border: `1px solid ${border}`,
      fontFamily: font,
      fontWeight: 600,
      fontSize: 11,
      letterSpacing: 0.3,
      padding: '3px 10px',
      borderRadius: 20,
      display: 'inline-block',
    }}>{label}</span>
  );
}

function CopyBtn({ text, dark }) {
  const [copied, copy] = useCopy(text);
  return (
    <button onClick={copy} style={{
      background: 'transparent',
      border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : C.border}`,
      color: dark ? '#8fb3cc' : C.textMuted,
      fontFamily: font,
      fontSize: 11,
      fontWeight: 500,
      padding: '4px 10px',
      borderRadius: 5,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: font,
      fontWeight: 700,
      fontSize: 10,
      letterSpacing: 1.8,
      color: C.brand,
      textTransform: 'uppercase',
      margin: '24px 0 10px',
      paddingBottom: 6,
      borderBottom: `1px solid ${C.border}`,
    }}>{children}</div>
  );
}

function EndpointBar({ method, path }) {
  const { url: BASE_URL } = useEnv();
  const full = BASE_URL + path;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      background: C.codeBase,
      padding: '12px 16px',
      borderRadius: 8,
      margin: '14px 0 18px',
      flexWrap: 'wrap',
    }}>
      <MethodPill method={method} />
      <code style={{ flex: 1, color: '#7dd3fc', fontFamily: mono, fontSize: 12.5, wordBreak: 'break-all' }}>
        {full}
      </code>
      <CopyBtn text={full} dark />
    </div>
  );
}

function CodePane({ title, code, status }) {
  const ok = status != null && status >= 200 && status < 300;
  return (
    <div style={{ borderRadius: 8, overflow: 'hidden', marginTop: 10 }}>
      <div style={{
        background: C.codeGutter,
        padding: '9px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: font, fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: 1.5, textTransform: 'uppercase' }}>{title}</span>
          {status != null && (
            <span style={{
              background: ok ? C.green : C.red,
              color: '#fff', fontFamily: mono,
              fontSize: 10, fontWeight: 700,
              padding: '2px 8px', borderRadius: 4,
            }}>{status}</span>
          )}
        </div>
        <CopyBtn text={code} dark />
      </div>
      <pre style={{
        background: C.codeBase,
        color: C.codeText,
        fontFamily: mono,
        fontSize: 12,
        lineHeight: 1.75,
        padding: '14px 18px',
        margin: 0,
        overflowX: 'auto',
        maxHeight: 420,
        textAlign: 'left',
        direction: 'ltr',
      }}>
        <code style={{ display: 'block', textAlign: 'left' }}>{code}</code>
      </pre>
    </div>
  );
}

function ParamTable({ rows, isResponse }) {
  if (!rows?.length) return <p style={{ fontFamily: font, color: C.textMuted, fontSize: 13, margin: '8px 0' }}>None.</p>;
  const cols = isResponse
    ? ['Field', 'Full Name', 'Type', 'Description']
    : ['Field', 'Full Name', 'Type', 'Description', 'Required'];
  return (
    <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${C.border}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: font, fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            {cols.map(h => (
              <th key={h} style={{
                textAlign: 'left',
                padding: '9px 14px',
                borderBottom: `1px solid ${C.border}`,
                color: C.textMuted,
                fontWeight: 600,
                fontSize: 10,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.name + i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' }}>
                <code style={{
                  background: '#f1f5f9',
                  color: C.navy,
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontFamily: mono,
                  fontSize: 12,
                  fontWeight: 500,
                }}>{r.name}</code>
              </td>
              <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, color: C.textMuted, verticalAlign: 'top', fontSize: 12 }}>{r.fullForm}</td>
              <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                <span style={{
                  background: C.blueBg,
                  color: C.blue,
                  border: `1px solid ${C.blueBorder}`,
                  padding: '2px 8px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontFamily: mono,
                  fontWeight: 500,
                }}>{r.type}</span>
              </td>
              <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, color: C.text, lineHeight: 1.55, verticalAlign: 'top' }}>
                {r.desc}
                {r.def && <span style={{ marginLeft: 6, color: C.textLight, fontSize: 12 }}>(default: {r.def})</span>}
              </td>
              {!isResponse && (
                <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                  {r.req
                    ? <span style={{ color: C.red, fontWeight: 600, fontSize: 12 }}>Required</span>
                    : <span style={{ color: C.textLight, fontSize: 12 }}>Optional</span>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Request Validator ──────────────────────────────────────────────── */

/* Saudi National Address short code: FOUR letters then FOUR digits, e.g. RRRD2929.
   Mirrors the backend check (NationalAddressUtils) that rejects a malformed code at order
   create with SC-299. Absent / null / blank is always valid - UAE and Qatar stops carry no
   national address at all, and nothing about those requests changes. */
const NATIONAL_ADDRESS_SHAPE = /^[A-Z]{4}[0-9]{4}$/;
function isNationalAddressShapeOk(value) {
  if (value === null || value === undefined) return true;
  const normalized = String(value).replace(/[\s-]/g, '').toUpperCase();
  if (normalized === '') return true;
  return NATIONAL_ADDRESS_SHAPE.test(normalized);
}

const VALIDATORS = {
  authentication: (body) => {
    const checks = [];
    const check = (id, label, pass, fix) => checks.push({ id, label, pass, fix });
    check('uname', 'uname (username) is present and non-empty', !!body.uname && String(body.uname).trim() !== '', 'Add "uname": "your-email@example.com"');
    check('pwd',   'pwd (password) is present and non-empty',   !!body.pwd   && String(body.pwd).trim()   !== '', 'Add "pwd": "YourPassword"');
    return checks;
  },

  booking: (body) => {
    const checks = [];
    const check = (id, label, pass, fix) => checks.push({ id, label, pass, fix });

    // Top-level required fields (GoOrderBusinessLogicImpl.java:476-484)
    check('cur',    'cur (currency) is present',               !!body.cur,    'Add "cur": "AED"');
    check('dlvcnt', 'dlvcnt (delivery count) is present',      body.dlvcnt != null, 'Add "dlvcnt": 1');
    check('pkpcnt', 'pkpcnt (pickup count) is present',        body.pkpcnt != null, 'Add "pkpcnt": 1');
    check('tamt',   'tamt (total amount) is present',          body.tamt != null, 'Add "tamt": 0');
    check('vtyp',   'vtyp is "Bike" or "Van"',                 ['Bike','Van'].includes(body.vtyp), 'Set "vtyp": "Bike" or "Van"');
    check('bktyp',  'bktyp is valid booking type',             ['Next Day','Same Day','On Demand'].includes(body.bktyp), 'Set "bktyp": "Next Day", "Same Day", or "On Demand"');
    check('chnl',   'chnl (channel) is present',               !!body.chnl, 'Add "chnl": "B2C"');
    check('ptyp',   'ptyp is "payatpickup" or "delivery"',     ['payatpickup','delivery'].includes(body.ptyp), 'Set "ptyp": "payatpickup" or "delivery"');
    check('dom',    'dom is "AE" or "QA"',                     ['AE','QA'].includes(body.dom), 'Set "dom": "AE" (Dubai) or "QA" (Qatar)');
    check('prid',   'prid (partner id) is present',            body.prid != null, 'Add "prid": <your partner ID>');
    check('pkp',    'pkp (pickup array) is present and non-empty', Array.isArray(body.pkp) && body.pkp.length > 0, 'Add "pkp": [{ ... }]');
    check('dlv',    'dlv (delivery array) is present and non-empty', Array.isArray(body.dlv) && body.dlv.length > 0, 'Add "dlv": [{ ... }]');

    // Delivery task validation (GoOrderBusinessLogicImpl.java:534-541) — COMMON FAILURE POINT
    if (Array.isArray(body.dlv)) {
      body.dlv.forEach((dlv, i) => {
        const pre = body.dlv.length > 1 ? `dlv[${i}]: ` : '';

        check(`dlv${i}_noitms`, `${pre}noitms (number of items) is present and non-null`,
          dlv.noitms != null && Number.isInteger(Number(dlv.noitms)),
          `Add "noitms": 1 inside your dlv object — this is the #1 cause of errorCode 3`);

        check(`dlv${i}_bkref`, `${pre}bkref (booking reference) is non-empty`,
          !!dlv.bkref && String(dlv.bkref).trim() !== '',
          'Add "bkref": "<your-order-reference>"');

        check(`dlv${i}_jd`, `${pre}jd (job description) is non-empty`,
          !!dlv.jd && String(dlv.jd).trim() !== '',
          'Add "jd": "Delivery description"');

        check(`dlv${i}_dimwghts`, `${pre}dimwghts array is present and non-empty`,
          Array.isArray(dlv.dimwghts) && dlv.dimwghts.length > 0,
          'Add "dimwghts": [{ "len": 1, "widt": 1, "heght": 1, "wght": 1 }]');

        if (Array.isArray(dlv.dimwghts) && dlv.dimwghts.length > 0) {
          const w = dlv.dimwghts[0];
          check(`dlv${i}_wght_type`, `${pre}dimwghts[0].wght is a number (not a string)`,
            typeof w.wght === 'number',
            `Change "wght": "${w.wght}" → "wght": ${parseFloat(w.wght) || 1} — must be a JSON number, not a quoted string`);
          check(`dlv${i}_wght_val`, `${pre}dimwghts[0].wght is greater than 0`,
            typeof w.wght === 'number' && w.wght > 0,
            'Set "wght" to a positive number e.g. 1');
          check(`dlv${i}_len`, `${pre}dimwghts[0].len is not null`,
            w.len != null, 'Add "len": 1 inside dimwghts[0]');
          check(`dlv${i}_heght`, `${pre}dimwghts[0].heght is not null`,
            w.heght != null, 'Add "heght": 1 inside dimwghts[0]');
        }

        check(`dlv${i}_add`,  `${pre}add (address) is present`,  !!dlv.add,  'Add "add": "Full address string"');
        check(`dlv${i}_lat`,  `${pre}lat (latitude) is present`,  dlv.lat != null, 'Add "lat": 25.2048');
        check(`dlv${i}_lon`,  `${pre}lon (longitude) is present`, dlv.lon != null, 'Add "lon": 55.2708');
        check(`dlv${i}_tim`,  `${pre}tim (datetime) is present and formatted correctly`,
          !!dlv.tim && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dlv.tim),
          'Format: "YYYY-MM-DD HH:mm:ss" e.g. "2026-05-22 14:00:00"');
        check(`dlv${i}_ph`,   `${pre}ph (phone) is present`,      !!dlv.ph,  'Add "ph": "971507000000"');
        check(`dlv${i}_name`, `${pre}name is present`,             !!dlv.name,'Add "name": "Customer Name"');
        check(`dlv${i}_hbid`, `${pre}hbid and teamid are both present`,
          !!dlv.hbid && !!dlv.teamid,
          'Add both "hbid": "timexpress-warehouse" and "teamid": "test_team_dubai" (see valid pairs table above)');

        check(`dlv${i}_nationalAddress`, `${pre}nationalAddress, when supplied, is 4 letters + 4 digits`,
          isNationalAddressShapeOk(dlv.nationalAddress),
          'Saudi National Address short code must be 4 letters then 4 digits, e.g. "RRRD2929" — otherwise create fails with SC-299. Omit the field entirely for UAE / Qatar stops.');
      });
    }

    // Pickup task checks
    if (Array.isArray(body.pkp)) {
      body.pkp.forEach((pkp, i) => {
        const pre = body.pkp.length > 1 ? `pkp[${i}]: ` : '';
        check(`pkp${i}_add`,  `${pre}add (address) is present`,  !!pkp.add,  'Add "add": "Pickup address"');
        check(`pkp${i}_lat`,  `${pre}lat is present`,             pkp.lat != null, 'Add "lat": 25.2048');
        check(`pkp${i}_lon`,  `${pre}lon is present`,             pkp.lon != null, 'Add "lon": 55.2708');
        check(`pkp${i}_tim`,  `${pre}tim is present and formatted`,
          !!pkp.tim && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(pkp.tim),
          'Format: "YYYY-MM-DD HH:mm:ss"');
        check(`pkp${i}_ph`,   `${pre}ph is present`,              !!pkp.ph,  'Add "ph": "971507000000"');
        check(`pkp${i}_hbid`, `${pre}hbid and teamid are both present`,
          !!pkp.hbid && !!pkp.teamid,
          'Add both "hbid" and "teamid" (see valid pairs table)');

        check(`pkp${i}_nationalAddress`, `${pre}nationalAddress, when supplied, is 4 letters + 4 digits`,
          isNationalAddressShapeOk(pkp.nationalAddress),
          'Saudi National Address short code must be 4 letters then 4 digits, e.g. "RRRD2929" — otherwise create fails with SC-299. Omit the field entirely for UAE / Qatar stops.');
      });
    }

    return checks;
  },

  cancellation: (_, qs) => {
    const checks = [];
    const check = (id, label, pass, fix) => checks.push({ id, label, pass, fix });
    const taskId = qs?.taskId || '';
    const reason = qs?.reason || '';
    check('taskId', 'taskId query param is present and non-empty', !!taskId.trim(), 'Add ?taskId=UB52-24B5533_test (pickup) or ?taskId=UB52-24B5533_1_test (delivery)');
    check('reason', 'reason query param is present and non-empty', !!reason.trim(), 'Add &reason=Your reason here');
    return checks;
  },

  amendment: (body) => {
    const checks = [];
    const check = (id, label, pass, fix) => checks.push({ id, label, pass, fix });
    // GoTasksBusinessLogicImpl.java:397-432
    check('ordid', 'ordid (order id) is present',     !!body.ordid, 'Add "ordid": "UB52-24B5533_1_test"');
    check('add',   'add (address) is present',         !!body.add,   'Add "add": "Full address"');
    check('lat',   'lat (latitude) is present',        body.lat != null, 'Add "lat": 25.2048');
    check('lon',   'lon (longitude) is present',       body.lon != null, 'Add "lon": 55.2708');
    check('tim',   'tim (time) is present and formatted correctly',
      !!body.tim && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(body.tim),
      'Format: "YYYY-MM-DD HH:mm:ss"');
    check('ph',    'ph (phone) is present and numeric (min 9 digits)',
      !!body.ph && /^\+?\d{9,15}$/.test(String(body.ph).replace(/\s/g,'')),
      'Phone must be numeric, at least 9 digits (remove spaces/dashes)');
    check('jd',    'jd (job description) is present',  !!body.jd,   'Add "jd": "Delivery"');
    check('name',  'name is present',                  !!body.name, 'Add "name": "Customer Name"');
    check('email', 'email is present',                  !!body.email,'Add "email": "customer@email.com"');
    check('jtyp',  'jtyp is "Pickup" or "Delivery"',
      ['Pickup','Delivery'].includes(body.jtyp),
      'Set "jtyp": "Pickup" or "Delivery"');
    check('dom',   'dom is "AE" or "QA"',              ['AE','QA'].includes(body.dom), 'Set "dom": "AE" or "QA"');
    return checks;
  },

  'task-status': (_, qs) => {
    const checks = [];
    checks.push({ id: 'bookingId', label: 'bookingId query param is present', pass: !!(qs?.bookingId || '').trim(), fix: 'Add ?bookingId=UB52-24B5533_1_test' });
    return checks;
  },

  'print-sticker': (_, qs) => {
    const checks = [];
    checks.push({ id: 'bookingId', label: 'bookingId query param is present', pass: !!(qs?.bookingId || '').trim(), fix: 'Add ?bookingId=UB52-24B5533_1_test' });
    return checks;
  },

  'booking-details': (body) => {
    const checks = [];
    const check = (id, label, pass, fix) => checks.push({ id, label, pass, fix });
    // GoTasksBusinessLogicImpl.java:3309-3319
    check('prid',    'prid (partner id) is present',    body.prid != null, 'Add "prid": <your partner ID>');
    check('frmdate', 'frmdate (from date) is present and formatted', !!body.frmdate && /^\d{4}-\d{2}-\d{2}$/.test(body.frmdate), 'Add "frmdate": "YYYY-MM-DD"');
    check('todate',  'todate (to date) is present and formatted',   !!body.todate  && /^\d{4}-\d{2}-\d{2}$/.test(body.todate),  'Add "todate": "YYYY-MM-DD"');
    if (body.frmdate && body.todate) {
      const diff = (new Date(body.todate) - new Date(body.frmdate)) / 86400000;
      check('daterange', 'Date range is 7 days or less',
        diff >= 0 && diff <= 7,
        `Range is ${Math.round(diff)} days. Maximum allowed is 7 days.`);
    }
    return checks;
  },
};

function RequestValidator({ section }) {
  const [open, setOpen]         = useState(false);
  const [input, setInput]       = useState('');
  const [results, setResults]   = useState(null);
  const [parseErr, setParseErr] = useState('');

  const isBodyEndpoint = section.method !== 'GET' && section.id !== 'cancellation' && section.id !== 'task-status' && section.id !== 'print-sticker';
  const isQueryOnly    = ['cancellation','task-status','print-sticker'].includes(section.id);

  const parseQueryString = (str) => {
    const out = {};
    str.replace(/^[?&]/, '').split('&').forEach(pair => {
      const [k, v] = pair.split('=');
      if (k) out[decodeURIComponent(k)] = decodeURIComponent(v || '');
    });
    return out;
  };

  const validate = () => {
    setParseErr('');
    const fn = VALIDATORS[section.id];
    if (!fn) { setResults([]); return; }

    let body = {}, qs = {};

    if (isQueryOnly) {
      // accept "?key=val&key2=val2" or just "key=val"
      qs = parseQueryString(input.trim());
    } else {
      // try parse as JSON
      try {
        body = JSON.parse(input);
      } catch (e) {
        setParseErr(`JSON parse error: ${e.message}`);
        return;
      }
      // also parse query string if appended after JSON (unlikely but handle gracefully)
    }

    setResults(fn(body, qs));
  };

  const passed  = results ? results.filter(r => r.pass).length  : 0;
  const failed  = results ? results.filter(r => !r.pass).length : 0;
  const allPass = results && failed === 0;

  const placeholder = isQueryOnly
    ? `Paste query string — e.g.:\ntaskId=UB52-24B5533_test&reason=test`
    : `Paste your request JSON body here — e.g.:\n${section.bodySample?.slice(0, 120) || '{ ... }'}`;

  return (
    <div style={{ marginTop: 16 }}>
      <button
        onClick={() => { setOpen(o => !o); setResults(null); setParseErr(''); }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '9px 18px', borderRadius: 8,
          border: `2px solid ${open ? '#dc2626' : '#2563eb'}`,
          background: open ? '#fef2f2' : '#eff6ff',
          color: open ? '#dc2626' : '#2563eb',
          fontFamily: font, fontWeight: 700, fontSize: 13,
          cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <span style={{ fontSize: 15 }}>⚡</span>
        {open ? 'Close Validator' : 'Validate Request'}
      </button>

      {open && (
        <div style={{
          marginTop: 10,
          border: `2px solid #2563eb`,
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            background: '#1e3a8a',
            padding: '10px 18px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontFamily: font, fontWeight: 700, fontSize: 12, color: '#bfdbfe', letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Request Validator — {section.title}
            </span>
            <span style={{ fontFamily: font, fontSize: 11, color: '#60a5fa' }}>
              Validation rules sourced directly from backend source code
            </span>
          </div>

          <div style={{ padding: 18, background: '#f0f7ff' }}>
            <div style={{ fontFamily: font, fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 8 }}>
              {isQueryOnly ? 'Paste query parameters:' : 'Paste your request JSON body:'}
            </div>
            <textarea
              value={input}
              onChange={e => { setInput(e.target.value); setResults(null); setParseErr(''); }}
              placeholder={placeholder}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 180,
                padding: '12px 14px',
                border: `1px solid ${parseErr ? '#fca5a5' : '#bfdbfe'}`,
                borderRadius: 7,
                fontFamily: mono, fontSize: 12, lineHeight: 1.7,
                boxSizing: 'border-box', resize: 'vertical',
                background: '#fff', color: C.text, outline: 'none',
              }}
            />

            {parseErr && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontFamily: font, fontSize: 12.5, color: '#dc2626' }}>
                {parseErr}
              </div>
            )}

            <button
              onClick={validate}
              disabled={!input.trim()}
              style={{
                marginTop: 10,
                padding: '10px 24px', border: 'none', borderRadius: 7,
                background: input.trim() ? '#2563eb' : '#cbd5e1',
                color: '#fff', fontFamily: font, fontWeight: 700, fontSize: 13,
                cursor: input.trim() ? 'pointer' : 'not-allowed', transition: 'all 0.15s',
              }}
            >
              Run Validation
            </button>
          </div>

          {/* Results */}
          {results && (
            <div style={{ background: '#fff', borderTop: '1px solid #bfdbfe' }}>
              {/* Summary bar */}
              <div style={{
                padding: '12px 18px',
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                background: allPass ? '#f0fdf4' : '#fef2f2',
                borderBottom: `1px solid ${allPass ? '#bbf7d0' : '#fecaca'}`,
              }}>
                <div style={{
                  fontFamily: font, fontWeight: 800, fontSize: 15,
                  color: allPass ? '#15803d' : '#dc2626',
                }}>
                  {allPass ? '✓ All checks passed' : `✗ ${failed} check${failed !== 1 ? 's' : ''} failed`}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <span style={{ fontFamily: font, fontSize: 12, fontWeight: 600, color: '#15803d', background: '#dcfce7', padding: '2px 10px', borderRadius: 20 }}>
                    {passed} passed
                  </span>
                  {failed > 0 && (
                    <span style={{ fontFamily: font, fontSize: 12, fontWeight: 600, color: '#dc2626', background: '#fee2e2', padding: '2px 10px', borderRadius: 20 }}>
                      {failed} failed
                    </span>
                  )}
                </div>
              </div>

              {/* Individual checks */}
              <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Show failures first */}
                {[...results.filter(r => !r.pass), ...results.filter(r => r.pass)].map(r => (
                  <div key={r.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '9px 12px', borderRadius: 7,
                    background: r.pass ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${r.pass ? '#bbf7d0' : '#fecaca'}`,
                  }}>
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                      background: r.pass ? '#16a34a' : '#dc2626',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: mono, fontSize: 12, fontWeight: 700, marginTop: 1,
                    }}>
                      {r.pass ? '✓' : '✗'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: font, fontSize: 13, fontWeight: r.pass ? 400 : 600, color: r.pass ? '#166534' : '#991b1b' }}>
                        {r.label}
                      </div>
                      {!r.pass && r.fix && (
                        <div style={{ marginTop: 4, fontFamily: mono, fontSize: 11.5, color: '#b91c1c', background: '#fee2e2', padding: '4px 8px', borderRadius: 4, lineHeight: 1.5 }}>
                          Fix: {r.fix}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Try it out panel ───────────────────────────────────────────────── */
function TryItOut({ section, token, setToken }) {
  const { url: BASE_URL, label: envLabel, color: envColor } = useEnv();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(section.bodySample || '');
  const [qs, setQs] = useState(section.queryParams || []);
  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState(null);
  const [respStatus, setRespStatus] = useState(null);
  const [err, setErr] = useState('');

  const updateQs = (i, k, v) => { const n = [...qs]; n[i] = { ...n[i], [k]: v }; setQs(n); };

  const fullUrl = useMemo(() => {
    const q = qs.filter(p => p.name && p.value !== '').map(p => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.value)}`).join('&');
    return `${BASE_URL}${section.path}${q ? '?' + q : ''}`;
  }, [qs, section.path, BASE_URL]);

  const execute = async () => {
    setLoading(true); setErr(''); setResp(null); setRespStatus(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (section.needsAuth) {
        if (!token) { setErr('No bearer token. Run the Authentication endpoint first, or paste a token in the token bar at the top.'); setLoading(false); return; }
        headers['Authorization'] = `Bearer ${token}`;
      }
      let data;
      if (section.method !== 'GET' && body.trim()) {
        try { data = JSON.parse(body); } catch (e) { setErr(`Invalid JSON in request body: ${e.message}`); setLoading(false); return; }
      }
      const res = await axios({ method: section.method, url: fullUrl, headers, data, validateStatus: () => true });
      setRespStatus(res.status);
      setResp(typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2));
      if (section.onResponse) section.onResponse(res.data, setToken);
    } catch (e) {
      setErr(`Network error: ${e.message}`);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ marginTop: 20 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: open ? C.navy : C.surface,
        color: open ? '#fff' : C.navy,
        border: `2px solid ${C.navy}`,
        borderRadius: 8,
        padding: '9px 20px',
        fontFamily: font,
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
        letterSpacing: 0.2,
      }}>
        {open ? 'Close' : 'Try it out'} {open ? '' : ''}
      </button>

      {open && (
        <div style={{
          marginTop: 12,
          border: `2px solid ${C.navy}`,
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          <div style={{
            background: C.navy,
            padding: '12px 20px',
            fontFamily: font,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 1.5,
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase',
          }}>
            Live Request
          </div>
          <div style={{ padding: 20, background: '#f8fafc' }}>

            {(section.queryParams || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: font, fontWeight: 600, fontSize: 11, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Query Parameters</div>
                {qs.map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <input value={p.name} onChange={e => updateQs(i, 'name', e.target.value)}
                      placeholder="key" style={{ ...inp, width: 200, fontFamily: mono, fontSize: 12 }} />
                    <input value={p.value} onChange={e => updateQs(i, 'value', e.target.value)}
                      placeholder="value" style={{ ...inp, flex: 1, minWidth: 200, fontFamily: mono, fontSize: 12 }} />
                  </div>
                ))}
              </div>
            )}

            {section.method !== 'GET' && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: font, fontWeight: 600, fontSize: 11, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                  Request Body <span style={{ fontWeight: 400, textTransform: 'none', fontSize: 11 }}>(JSON)</span>
                </div>
                <textarea value={body} onChange={e => setBody(e.target.value)} spellCheck={false} style={{
                  width: '100%',
                  minHeight: 200,
                  padding: '12px 14px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 7,
                  fontFamily: mono,
                  fontSize: 12,
                  lineHeight: 1.7,
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  background: '#fff',
                  color: C.codeBase,
                  outline: 'none',
                }} />
              </div>
            )}

            <div style={{ marginBottom: 14, fontFamily: font, fontSize: 12, color: C.textMuted }}>
              <strong>Endpoint:</strong>{' '}
              <code style={{ fontFamily: mono, color: C.brandDark, fontSize: 12 }}>{section.method} {fullUrl}</code>
            </div>

            <button onClick={execute} disabled={loading} style={{
              background: loading ? C.textLight : C.brand,
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '10px 24px',
              fontFamily: font,
              fontWeight: 600,
              fontSize: 13,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}>
              {loading
                ? <><span style={{ display: 'inline-block', width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Sending</>
                : 'Execute'}
            </button>

            {err && (
              <div style={{
                marginTop: 12, background: C.redBg, border: `1px solid ${C.redBorder}`,
                borderRadius: 7, padding: '10px 14px',
                fontFamily: font, fontSize: 13, color: C.red, lineHeight: 1.5,
              }}>{err}</div>
            )}

            {resp != null && (
              <div style={{ marginTop: 14 }}>
                <CodePane title="Live Response" code={resp} status={respStatus} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inp = {
  padding: '9px 12px',
  border: `1px solid ${C.border}`,
  borderRadius: 7,
  fontSize: 13,
  background: '#fff',
  outline: 'none',
  color: C.text,
};

/* ─── Onboarding section ─────────────────────────────────────────────── */
function Onboarding() {
  const steps = [
    {
      num: '01',
      title: 'Submit Your Details to the ShypV Sales Team',
      body: (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 16 }}>
          <div>
            <div style={{ fontFamily: font, fontWeight: 600, fontSize: 12, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
              Personal Details
            </div>
            {[
              { label: 'Full Name', placeholder: 'e.g. Mohammed Al-Rashid' },
              { label: 'Email Address', placeholder: 'e.g. name@company.com' },
              { label: 'Phone Number', placeholder: 'e.g. +971 50 000 0000' },
            ].map(f => (
              <div key={f.label} style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: font, fontSize: 12, fontWeight: 500, color: C.text, marginBottom: 4 }}>{f.label}</div>
                <div style={{
                  background: '#f8fafc',
                  border: `1px solid ${C.border}`,
                  borderRadius: 7,
                  padding: '10px 14px',
                  fontFamily: font,
                  fontSize: 12,
                  color: C.textLight,
                }}>{f.placeholder}</div>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontFamily: font, fontWeight: 600, fontSize: 12, color: C.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
              Company Details
            </div>
            {[
              { label: 'Official Company Name', placeholder: 'e.g. Logistics Corp LLC' },
              { label: 'Company Telephone', placeholder: 'e.g. +971 4 000 0000' },
              { label: 'Project Name', placeholder: 'e.g. E-commerce Delivery Integration' },
              { label: 'Location', placeholder: 'e.g. Dubai, UAE' },
              { label: 'Pin Code / Postal Code', placeholder: 'e.g. 12345' },
            ].map(f => (
              <div key={f.label} style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: font, fontSize: 12, fontWeight: 500, color: C.text, marginBottom: 4 }}>{f.label}</div>
                <div style={{
                  background: '#f8fafc',
                  border: `1px solid ${C.border}`,
                  borderRadius: 7,
                  padding: '10px 14px',
                  fontFamily: font,
                  fontSize: 12,
                  color: C.textLight,
                }}>{f.placeholder}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      num: '02',
      title: 'Receive Your Credentials via Email',
      body: (
        <div style={{ marginTop: 14, fontFamily: font, fontSize: 14, color: C.text, lineHeight: 1.75 }}>
          <p style={{ margin: '0 0 12px' }}>
            Once your onboarding request is reviewed, ShypV will generate a secure account for your organisation and send the following to your registered email address:
          </p>
          <ul style={{ margin: 0, paddingLeft: 22, lineHeight: 2 }}>
            <li><strong>Username</strong> — your registered email address</li>
            <li><strong>Password</strong> — a system-generated password (change it on first login)</li>
            <li><strong>Partner ID (prid)</strong> — your numeric partner identifier, required for booking APIs</li>
            <li><strong>Environment URL</strong> — staging and production base URLs</li>
          </ul>
          <div style={{
            marginTop: 14,
            background: C.amberBg,
            border: `1px solid ${C.amberBorder}`,
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 13,
            color: C.amber,
          }}>
            Check your spam or junk folder if the email does not arrive within 24 hours. Contact support if you experience any issues.
          </div>
        </div>
      ),
    },
    {
      num: '03',
      title: 'Authenticate and Obtain an Access Token',
      body: (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontFamily: font, fontSize: 14, color: C.text, lineHeight: 1.7, margin: '0 0 14px' }}>
            Call the <strong>Authentication</strong> endpoint with the credentials you received. A JWT bearer token will be returned. Include this token in the <code style={{ fontFamily: mono, fontSize: 13, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>Authorization</code> header for all subsequent API requests.
          </p>
          <CodePane title="Authentication Request" code={`POST https://staging-api.shypv.com/authenticate

{
  "uname": "partner@example.com",
  "pwd": "YourPassword123"
}`} />
          <div style={{
            marginTop: 12,
            background: C.greenBg,
            border: `1px solid ${C.greenBorder}`,
            borderRadius: 8,
            padding: '12px 16px',
            fontFamily: font,
            fontSize: 13,
            color: C.green,
          }}>
            The token returned in <code style={{ fontFamily: mono }}>data.tkn</code> is valid for approximately 10 hours. Re-authenticate before expiry to avoid disruption.
          </div>
        </div>
      ),
    },
  ];

  const [openStep, setOpenStep] = useState(0);

  return (
    <section id="getting-started" style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 24,
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      scrollMarginTop: 86,
    }}>
      <div style={{
        background: C.navy,
        padding: '22px 28px',
      }}>
        <div style={{ fontFamily: font, fontWeight: 800, fontSize: 20, color: '#fff', marginBottom: 6 }}>Getting Started</div>
        <div style={{ fontFamily: font, fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
          Follow these three steps to gain API access and make your first request.
        </div>
      </div>

      <div style={{ padding: '4px 0' }}>
        {steps.map((s, i) => (
          <div key={i} style={{ borderBottom: i < steps.length - 1 ? `1px solid ${C.border}` : 'none' }}>
            <button
              onClick={() => setOpenStep(openStep === i ? -1 : i)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: '18px 28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                textAlign: 'left',
              }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: openStep === i ? C.brand : '#f1f5f9',
                color: openStep === i ? '#fff' : C.textMuted,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: font, fontWeight: 700, fontSize: 13,
                transition: 'all 0.2s',
              }}>
                {s.num}
              </div>
              <div style={{ flex: 1, fontFamily: font, fontWeight: 600, fontSize: 14, color: C.text }}>{s.title}</div>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <span style={{
                  display: 'inline-block',
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: openStep === i ? 'none' : `6px solid ${C.textMuted}`,
                  borderBottom: openStep === i ? `6px solid ${C.textMuted}` : 'none',
                  marginTop: openStep === i ? 2 : -2,
                }} />
              </div>
            </button>
            {openStep === i && (
              <div style={{ padding: '0 28px 24px' }}>{s.body}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Section card ───────────────────────────────────────────────────── */
function SectionCard({ section, token, setToken, active }) {
  const { url: BASE_URL } = useEnv();
  const url = BASE_URL + section.path;
  return (
    <div id={section.id} style={{
      background: C.surface,
      border: `1px solid ${active ? C.brand : C.border}`,
      borderRadius: 12,
      overflow: 'hidden',
      marginBottom: 24,
      boxShadow: active ? `0 0 0 3px ${C.brand}22` : '0 1px 4px rgba(0,0,0,0.05)',
      transition: 'border-color 0.3s, box-shadow 0.3s',
      scrollMarginTop: 86,
    }}>
      {/* Card header bar */}
      <div style={{
        padding: '20px 24px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: `linear-gradient(135deg, ${C.brand}, ${C.brandDark})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontFamily: font, fontWeight: 800, fontSize: 14,
        }}>
          {section.number}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h2 style={{ fontFamily: font, fontWeight: 700, fontSize: 19, color: C.text, margin: 0 }}>{section.title}</h2>
            <MethodPill method={section.method} />
            <Tag label={section.tag} color={section.tagColor} bg={section.tagBg} border={section.tagBorder} />
            {section.needsAuth && (
              <span style={{
                background: '#fff7ed',
                color: C.amber,
                border: `1px solid ${C.amberBorder}`,
                fontFamily: font, fontSize: 11, fontWeight: 600,
                padding: '2px 8px', borderRadius: 20,
              }}>Requires Auth</span>
            )}
          </div>
          <p style={{ fontFamily: font, fontSize: 13.5, color: C.textMuted, margin: 0, lineHeight: 1.65 }}>{section.purpose}</p>
        </div>
      </div>

      <div style={{ padding: '0 24px 24px' }}>
        <SectionLabel>Endpoint</SectionLabel>
        <EndpointBar method={section.method} path={section.path} />

        {section.note && (
          <div style={{
            background: C.blueBg,
            border: `1px solid ${C.blueBorder}`,
            borderRadius: 7,
            padding: '10px 14px',
            fontFamily: font,
            fontSize: 13,
            color: C.blue,
            lineHeight: 1.55,
            marginBottom: 16,
          }}>
            <strong>Note:</strong> {section.note}
          </div>
        )}

        <SectionLabel>Request Parameters</SectionLabel>
        <ParamTable rows={section.requestParams} />

        {section.taskParams && (
          <>
            <SectionLabel>Task Object Fields (pkp / dlv items)</SectionLabel>
            <ParamTable rows={section.taskParams} />
          </>
        )}

        {section.nationalAddressNote && (
          <>
            <SectionLabel>{section.nationalAddressNote.title}</SectionLabel>
            <div style={{
              background: C.blueBg,
              border: `1px solid ${C.blueBorder}`,
              borderRadius: 8,
              padding: '12px 16px',
              fontFamily: font,
              fontSize: 13,
              color: C.blue,
              lineHeight: 1.6,
            }}>
              {(section.nationalAddressNote.lines || []).map((line, i, all) => (
                <p key={i} style={{ margin: i === all.length - 1 ? 0 : '0 0 8px' }}>{line}</p>
              ))}
            </div>
            {(section.nationalAddressNote.requestSample || section.nationalAddressNote.errorSample) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <CodePane title="dlv object for a Saudi stop" code={section.nationalAddressNote.requestSample || ''} />
                <CodePane title="Malformed code: rejected at create" code={section.nationalAddressNote.errorSample || ''} />
              </div>
            )}
          </>
        )}

        {section.hbidTeamPairs && (
          <>
            <SectionLabel>hbid & teamid — Valid Pairs</SectionLabel>
            <div style={{
              background: C.blueBg,
              border: `1px solid ${C.blueBorder}`,
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 12,
              fontFamily: font,
              fontSize: 13,
              color: C.blue,
              lineHeight: 1.6,
            }}>
              <strong>What are these?</strong>
              <br />
              <strong>hbid (Homebase ID)</strong> — identifies the physical warehouse or depot in the Locus dispatch system that will handle this task. It determines which hub's drivers and route plans are used.
              <br />
              <strong>teamid (Team ID)</strong> — identifies the operational dispatch team within that homebase. Both fields must be provided together and must be a valid pair — if either is missing or mismatched the task will not be routed.
            </div>
            <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${C.border}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: font, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Location / Operation', 'hbid value', 'teamid value', 'Environment'].map(h => (
                      <th key={h} style={{
                        textAlign: 'left', padding: '9px 14px',
                        borderBottom: `1px solid ${C.border}`,
                        color: C.textMuted, fontWeight: 700, fontSize: 10,
                        letterSpacing: 0.8, textTransform: 'uppercase', whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.hbidTeamPairs.map((pair, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, color: C.text, fontWeight: 500 }}>
                        {pair.location}
                      </td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                        <code style={{
                          background: '#f1f5f9', color: C.navy,
                          padding: '2px 8px', borderRadius: 4,
                          fontFamily: mono, fontSize: 12, fontWeight: 600,
                        }}>{pair.hbid}</code>
                      </td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                        <code style={{
                          background: '#f1f5f9', color: C.navy,
                          padding: '2px 8px', borderRadius: 4,
                          fontFamily: mono, fontSize: 12, fontWeight: 600,
                        }}>{pair.teamid}</code>
                      </td>
                      <td style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                        <span style={{
                          fontFamily: font, fontSize: 11, fontWeight: 600,
                          padding: '2px 9px', borderRadius: 20,
                          background: pair.env === 'Production' ? C.greenBg : pair.env === 'Staging' ? C.amberBg : C.blueBg,
                          color: pair.env === 'Production' ? C.green : pair.env === 'Staging' ? C.amber : C.blue,
                          border: `1px solid ${pair.env === 'Production' ? C.greenBorder : pair.env === 'Staging' ? C.amberBorder : C.blueBorder}`,
                        }}>{pair.env}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{
              marginTop: 10,
              background: '#fffbeb',
              border: `1px solid ${C.amberBorder}`,
              borderRadius: 7,
              padding: '10px 14px',
              fontFamily: font,
              fontSize: 12,
              color: C.amber,
              lineHeight: 1.6,
            }}>
              <strong>Important:</strong> Contact the ShypV integration team to confirm which hbid/teamid pair applies to your account. Using the wrong pair will cause tasks to be routed to the incorrect hub.
            </div>
          </>
        )}

        <SectionLabel>Response Fields</SectionLabel>
        <ParamTable rows={section.responseParams} isResponse />

        {section.statuses && (
          <>
            <SectionLabel>Task Status Reference</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {section.statuses.map(s => (
                <div key={s.code} style={{
                  border: `1px solid ${s.color}30`,
                  borderRadius: 8,
                  padding: '10px 16px',
                  background: s.color + '0c',
                  minWidth: 170,
                }}>
                  <div style={{ fontFamily: mono, fontWeight: 600, fontSize: 12, color: s.color, marginBottom: 4 }}>{s.code}</div>
                  <div style={{ fontFamily: font, fontSize: 12, color: C.textMuted, lineHeight: 1.4 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <SectionLabel>Sample Request &amp; Response</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <CodePane
            title="REQUEST"
            code={
              section.bodySample
                ? section.bodySample
                : `${section.method} ${url}${section.queryParams?.length ? '?' + section.queryParams.map(p => `${p.name}=${p.value}`).join('&') : ''}`
            }
          />
          <CodePane title="RESPONSE" code={section.responseSample} />
        </div>

        {/* Validation note (backend-verified) */}
        {section.validationNote && (
          <div style={{
            marginTop: 16,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '0 8px 8px 0',
            padding: '12px 16px',
            fontFamily: font,
            fontSize: 12.5,
            color: '#78350f',
            lineHeight: 1.65,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: '#92400e', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Validation Rules — Verified from Backend Source
            </div>
            {section.validationNote}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <SectionLabel>Try It Out</SectionLabel>
            <TryItOut section={section} token={token} setToken={setToken} />
          </div>
          {VALIDATORS[section.id] && (
            <div style={{ flex: 1, minWidth: 280 }}>
              <SectionLabel>Validate Request</SectionLabel>
              <RequestValidator section={section} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── LLM Instruction card ──────────────────────────────────────────── */
function buildLLMInstruction(baseUrl) {
  const lines = [];
  lines.push('# ShypV TMS API — Integration Instructions for AI Assistant');
  lines.push('');
  lines.push('You are helping a developer integrate with the ShypV Transport Management System API.');
  lines.push(`Base URL: ${baseUrl}`);
  lines.push('Authentication: POST /authenticate → returns data.tkn. Pass as "Authorization: Bearer <tkn>" header on all protected endpoints.');
  lines.push('Content-Type: application/json for all requests.');
  lines.push('Token validity: ~10 hours. Re-authenticate on expiry.');
  lines.push('');
  lines.push('## Onboarding Steps');
  lines.push('1. Partner submits personal details (full name, email, phone) and company details (official name, telephone, project name, location, pin code) to the ShypV sales team.');
  lines.push('2. ShypV generates an account and emails credentials: username (email), password, partner ID (prid), and environment URLs.');
  lines.push('3. Partner calls POST /authenticate with {uname, pwd} to receive the bearer token (data.tkn).');
  lines.push('');

  SECTIONS.forEach((s, idx) => {
    lines.push(`## ${idx + 1}. ${s.title}`);
    lines.push(`Method: ${s.method}`);
    lines.push(`Endpoint: ${baseUrl}${s.path}`);
    lines.push(`Auth required: ${s.needsAuth ? 'Yes — Authorization: Bearer <token>' : 'No'}`);
    lines.push(`Purpose: ${s.purpose}`);
    lines.push('');

    if (s.requestParams?.length) {
      lines.push('### Request Parameters');
      s.requestParams.forEach(p => {
        const req = p.req === true ? 'Required' : p.req === false ? 'Optional' : p.req || 'Optional';
        lines.push(`- ${p.name} (${p.type}) [${req}]: ${p.desc}${p.def ? ` Default: ${p.def}.` : ''}`);
      });
      lines.push('');
    }

    if (s.taskParams?.length) {
      lines.push('### Task Object Fields (used inside pkp[] and dlv[] arrays)');
      s.taskParams.forEach(p => {
        const req = p.req === true ? 'Required' : p.req === false ? 'Optional' : p.req || 'Optional';
        lines.push(`- ${p.name} (${p.type}) [${req}]: ${p.desc}${p.def ? ` Default: ${p.def}.` : ''}`);
      });
      lines.push('');
    }

    if (s.nationalAddressNote) {
      lines.push(`### ${s.nationalAddressNote.title}`);
      (s.nationalAddressNote.lines || []).forEach(line => lines.push(`- ${line}`));
      if (s.nationalAddressNote.errorSample) {
        lines.push('Rejected response:');
        lines.push('```json');
        lines.push(s.nationalAddressNote.errorSample.trim());
        lines.push('```');
      }
      lines.push('');
    }

    if (s.hbidTeamPairs?.length) {
      lines.push('### hbid (Homebase ID) and teamid (Team ID) — Valid Pairs');
      lines.push('hbid identifies the physical warehouse/depot in the Locus dispatch system.');
      lines.push('teamid identifies the dispatch team at that hub. Both MUST be provided together as a matching pair.');
      lines.push('');
      s.hbidTeamPairs.forEach(p => {
        lines.push(`- Location: ${p.location} | hbid: "${p.hbid}" | teamid: "${p.teamid}" | Environment: ${p.env}`);
      });
      lines.push('Contact the ShypV integration team to confirm which pair applies to your account.');
      lines.push('');
    }

    if (s.responseParams?.length) {
      lines.push('### Response Fields');
      s.responseParams.forEach(p => {
        lines.push(`- ${p.name} (${p.type}): ${p.desc}`);
      });
      lines.push('');
    }

    if (s.statuses?.length) {
      lines.push('### Task Status Values');
      s.statuses.forEach(st => lines.push(`- ${st.code}: ${st.desc}`));
      lines.push('');
    }

    if (s.bodySample?.trim()) {
      lines.push('### Sample Request');
      lines.push('```json');
      lines.push(s.bodySample.trim());
      lines.push('```');
      lines.push('');
    }

    lines.push('### Sample Response');
    lines.push('```json');
    lines.push(s.responseSample.trim());
    lines.push('```');
    lines.push('');
    lines.push('---');
    lines.push('');
  });

  lines.push('## Standard Response Structure');
  lines.push('Every endpoint returns: { "data": <payload>, "info": null, "errors": [], "success": true/false }');
  lines.push('Always check the "success" field. If false, read "errors" array for details.');
  lines.push('');
  lines.push('## Important Notes');
  lines.push('- Booking IDs format: e.g. UB52-24B5533');
  lines.push('- Task ID for pickup: bookingId_test (staging) or bookingId (production)');
  lines.push('- Task ID for delivery: bookingId_1_test (staging) or bookingId_1 (production)');
  lines.push('- Date format: YYYY-MM-DD. DateTime format: YYYY-MM-DD HH:mm:ss');
  lines.push('- Phone numbers must be 7–15 characters');
  lines.push('- Maximum date range for booking list: 7 days');
  lines.push('- Staging base URL: https://staging-api.shypv.com');
  lines.push('- Production base URL: https://api.shypv.com');
  lines.push('- Partner Portal (Staging): https://business-agent-stage.shypv.com/');
  lines.push('- Partner Portal (Production): https://business-agent.shypv.com/');

  return lines.join('\n');
}

function buildPartInstruction(baseUrl, partIndex) {
  const PARTS = [
    { label: 'Part 1 of 3', apis: [0, 1, 2], note: 'Contains: Authentication, Create Booking, Cancel Booking' },
    { label: 'Part 2 of 3', apis: [3, 4],    note: 'Contains: Edit Booking, Task Status' },
    { label: 'Part 3 of 3', apis: [5, 6],    note: 'Contains: Print Sticker (AWB), Booking Details' },
  ];
  const part = PARTS[partIndex];
  const lines = [];
  lines.push(`# ShypV TMS API — Integration Instructions (${part.label})`);
  lines.push(`# ${part.note}`);
  lines.push('');
  lines.push('You are helping a developer integrate with the ShypV Transport Management System API.');
  lines.push(`Base URL: ${baseUrl}`);
  lines.push('Authentication: POST /authenticate → returns data.tkn. Pass as "Authorization: Bearer <tkn>" header.');
  lines.push('Content-Type: application/json. Token validity: ~10 hours.');
  lines.push('Standard response: { "data": <payload>, "info": null, "errors": [], "success": true/false }');
  lines.push('');

  if (partIndex === 0) {
    lines.push('## Onboarding');
    lines.push('1. Submit personal + company details to ShypV sales team.');
    lines.push('2. ShypV emails credentials (username/email, password, partner ID, env URLs).');
    lines.push('3. Call POST /authenticate to get bearer token (data.tkn).');
    lines.push('');
  }

  part.apis.forEach(idx => {
    const s = SECTIONS[idx];
    lines.push(`## ${s.title}`);
    lines.push(`${s.method} ${baseUrl}${s.path}`);
    lines.push(`Auth: ${s.needsAuth ? 'Bearer token required' : 'None'}`);
    lines.push(`Purpose: ${s.purpose}`);
    lines.push('');
    if (s.requestParams?.length) {
      lines.push('Request Parameters:');
      s.requestParams.forEach(p => {
        const req = p.req === true ? 'Required' : p.req === false ? 'Optional' : (p.req || 'Optional');
        lines.push(`  ${p.name} (${p.type}) [${req}]: ${p.desc}`);
      });
      lines.push('');
    }
    if (s.taskParams?.length) {
      lines.push('Task Object Fields (pkp[] / dlv[] items):');
      s.taskParams.forEach(p => {
        const req = p.req === true ? 'Required' : p.req === false ? 'Optional' : (p.req || 'Optional');
        lines.push(`  ${p.name} (${p.type}) [${req}]: ${p.desc}`);
      });
      lines.push('');
    }

    if (s.nationalAddressNote) {
      lines.push(`${s.nationalAddressNote.title}:`);
      (s.nationalAddressNote.lines || []).forEach(line => lines.push(`  ${line}`));
      lines.push('');
    }
    if (s.hbidTeamPairs?.length) {
      lines.push('hbid / teamid Valid Pairs:');
      s.hbidTeamPairs.forEach(p => lines.push(`  hbid="${p.hbid}" + teamid="${p.teamid}" → ${p.location} [${p.env}]`));
      lines.push('');
    }
    if (s.responseParams?.length) {
      lines.push('Response Fields:');
      s.responseParams.forEach(p => lines.push(`  ${p.name} (${p.type}): ${p.desc}`));
      lines.push('');
    }
    if (s.statuses?.length) {
      lines.push('Status Values:');
      s.statuses.forEach(st => lines.push(`  ${st.code}: ${st.desc}`));
      lines.push('');
    }
    if (s.bodySample?.trim()) {
      lines.push('Sample Request:');
      lines.push('```json'); lines.push(s.bodySample.trim()); lines.push('```');
      lines.push('');
    }
    lines.push('Sample Response:');
    lines.push('```json'); lines.push(s.responseSample.trim()); lines.push('```');
    lines.push(''); lines.push('---'); lines.push('');
  });

  if (partIndex === 2) {
    lines.push('## Important Notes');
    lines.push('- Booking ID format: e.g. UB52-24B5533');
    lines.push('- Task ID pickup: bookingId_test (staging) / bookingId (prod)');
    lines.push('- Task ID delivery: bookingId_1_test (staging) / bookingId_1 (prod)');
    lines.push('- Date: YYYY-MM-DD | DateTime: YYYY-MM-DD HH:mm:ss');
    lines.push('- Phone: 7–15 characters | Booking list max range: 7 days');
    lines.push('- Staging: https://staging-api.shypv.com');
    lines.push('- Production: https://api.shypv.com');
  }

  return lines.join('\n');
}

function LLMInstructionCard({ baseUrl }) {
  const [copiedKey, setCopiedKey] = useState('');
  const [previewPart, setPreviewPart] = useState('full'); // 'full' | 0 | 1 | 2

  const full = useMemo(() => buildLLMInstruction(baseUrl), [baseUrl]);
  const parts = useMemo(() => [0, 1, 2].map(i => buildPartInstruction(baseUrl, i)), [baseUrl]);

  const previewText = previewPart === 'full' ? full : parts[previewPart];

  const estTokens = t => Math.round(t.length / 4);
  const fmtK = n => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n;

  const doCopy = (key, text) => {
    try { navigator.clipboard.writeText(text); } catch (_) {}
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(''), 2500);
  };

  const PART_LABELS = [
    { label: 'Part 1', sub: 'Auth · Booking · Cancel', color: '#7c3aed', apis: 'APIs 1–3' },
    { label: 'Part 2', sub: 'Edit · Status',            color: '#2563eb', apis: 'APIs 4–5' },
    { label: 'Part 3', sub: 'AWB · Details',            color: '#059669', apis: 'APIs 6–7' },
  ];

  return (
    <div style={{
      borderRadius: 14,
      overflow: 'hidden',
      marginBottom: 24,
      border: '1px solid #6d28d944',
      boxShadow: '0 4px 28px rgba(109,40,217,0.12)',
    }}>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position: 600px 0; }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.78); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50%       { transform: translateY(-5px) rotate(8deg); }
        }
        @keyframes fadeInPrompt {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Two-column body ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', minHeight: 360 }}>

        {/* LEFT — info panel */}
        <div style={{
          background: 'linear-gradient(160deg, #1e1b4b 0%, #3b0764 60%, #1e1b4b 100%)',
          backgroundSize: '1200px 100%',
          animation: 'shimmer 6s linear infinite',
          padding: '26px 26px 22px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ position: 'absolute', top: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(167,139,250,0.10)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -20, right: 20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(167,139,250,0.07)', pointerEvents: 'none' }} />

          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, position: 'relative' }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, flexShrink: 0,
              background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, animation: 'float 3.5s ease-in-out infinite',
              boxShadow: '0 0 20px rgba(167,139,250,0.4)',
            }}>✦</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: font, fontWeight: 800, fontSize: 16, color: '#fff' }}>Copy Instructions to AI</span>
                <span style={{ background: 'rgba(167,139,250,0.22)', border: '1px solid rgba(167,139,250,0.4)', color: '#c4b5fd', fontFamily: mono, fontWeight: 700, fontSize: 9, padding: '2px 7px', borderRadius: 20, letterSpacing: 0.8 }}>LLM PROMPT</span>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#a3e635', display: 'inline-block', animation: 'pulse-dot 2s ease-in-out infinite', boxShadow: '0 0 6px #a3e635' }} />
              </div>
              <div style={{ fontFamily: font, fontSize: 11, color: 'rgba(196,181,253,0.65)', marginTop: 2 }}>
                ChatGPT · Claude · Gemini · any AI
              </div>
            </div>
          </div>

          {/* Description */}
          <p style={{ fontFamily: font, fontSize: 12.5, color: 'rgba(196,181,253,0.88)', lineHeight: 1.65, margin: '0 0 16px', position: 'relative' }}>
            Paste this into any AI assistant to get help writing integration code, debugging requests, or understanding any ShypV TMS API field.
          </p>

          {/* Context size */}
          <div style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(167,139,250,0.2)',
            borderRadius: 9, padding: '12px 14px', marginBottom: 16, position: 'relative',
          }}>
            <div style={{ fontFamily: font, fontSize: 10, fontWeight: 700, color: 'rgba(196,181,253,0.6)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>Context Size</div>
            <div style={{ display: 'flex', gap: 16 }}>
              {[
                ['~' + fmtK(estTokens(full)), 'tokens (full)'],
                [full.split('\n').length, 'lines'],
                [SECTIONS.length, 'endpoints'],
              ].map(([val, lbl]) => (
                <div key={lbl}>
                  <div style={{ fontFamily: font, fontWeight: 800, fontSize: 18, color: '#e9d5ff' }}>{val}</div>
                  <div style={{ fontFamily: font, fontSize: 10, color: 'rgba(196,181,253,0.55)' }}>{lbl}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { name: 'GPT-4o', limit: '128k', fits: estTokens(full) < 128000 },
                { name: 'Claude', limit: '200k', fits: estTokens(full) < 200000 },
                { name: 'Gemini', limit: '1M',   fits: true },
              ].map(m => (
                <span key={m.name} style={{
                  fontFamily: font, fontSize: 10, fontWeight: 600,
                  padding: '2px 8px', borderRadius: 20,
                  background: m.fits ? 'rgba(163,230,53,0.15)' : 'rgba(239,68,68,0.15)',
                  color: m.fits ? '#a3e635' : '#f87171',
                  border: `1px solid ${m.fits ? 'rgba(163,230,53,0.3)' : 'rgba(239,68,68,0.3)'}`,
                }}>
                  {m.fits ? '✓' : '✗'} {m.name} ({m.limit})
                </span>
              ))}
            </div>
          </div>

          {/* ── Copy full prompt ── */}
          <div style={{ marginBottom: 14, position: 'relative' }}>
            <div style={{ fontFamily: font, fontSize: 10, fontWeight: 700, color: 'rgba(196,181,253,0.6)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>Full Prompt (all 7 APIs)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => { doCopy('full', full); setPreviewPart('full'); }}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 8, border: 'none',
                  background: copiedKey === 'full' ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#7c3aed,#6d28d9)',
                  color: '#fff', fontFamily: font, fontWeight: 700, fontSize: 13,
                  cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: copiedKey === 'full' ? '0 0 14px rgba(22,163,74,0.4)' : '0 0 14px rgba(124,58,237,0.4)',
                }}
              >{copiedKey === 'full' ? '✓ Copied!' : '⎘ Copy Full Prompt'}</button>
            </div>
          </div>

          {/* ── Split into 3 parts ── */}
          <div style={{ position: 'relative', marginTop: 'auto' }}>
            <div style={{ fontFamily: font, fontSize: 10, fontWeight: 700, color: 'rgba(196,181,253,0.6)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 7 }}>
              Or split into 3 prompts (smaller context)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {PART_LABELS.map((p, i) => (
                <div key={p.label} style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: p.color, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, fontFamily: font, fontSize: 12, color: '#c4b5fd', fontWeight: 500 }}>
                    <strong style={{ color: '#e9d5ff' }}>{p.label}</strong>
                    <span style={{ color: 'rgba(196,181,253,0.6)', marginLeft: 6, fontSize: 11 }}>{p.apis} · {p.sub}</span>
                    <span style={{ color: 'rgba(196,181,253,0.45)', marginLeft: 6, fontSize: 10 }}>~{fmtK(estTokens(parts[i]))} tokens</span>
                  </div>
                  <button
                    onClick={() => { doCopy('part' + i, parts[i]); setPreviewPart(i); }}
                    style={{
                      padding: '4px 12px', borderRadius: 6, border: 'none',
                      background: copiedKey === 'part' + i ? p.color + 'dd' : p.color + '22',
                      color: copiedKey === 'part' + i ? '#fff' : p.color,
                      fontFamily: font, fontSize: 11, fontWeight: 700,
                      cursor: 'pointer', transition: 'all 0.2s',
                      border: `1px solid ${p.color}55`,
                    }}
                  >{copiedKey === 'part' + i ? '✓' : 'Copy'}</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT — prompt always visible */}
        <div style={{ display: 'flex', flexDirection: 'column', background: C.codeBase }}>
          {/* Header bar */}
          <div style={{
            background: '#1e1b4b',
            padding: '10px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid rgba(167,139,250,0.2)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {['#ff5f57','#febc2e','#28c840'].map(col => (
                <span key={col} style={{ width: 10, height: 10, borderRadius: '50%', background: col, display: 'inline-block' }} />
              ))}
              <span style={{ fontFamily: mono, fontSize: 11, color: '#a78bfa', letterSpacing: 0.8, marginLeft: 6 }}>
                {previewPart === 'full' ? 'shypv-api-instructions.md' : `shypv-api-part${Number(previewPart) + 1}.md`}
              </span>
            </div>
            <span style={{ fontFamily: font, fontSize: 11, color: '#4b5563' }}>
              {previewText.split('\n').length} lines · ~{fmtK(estTokens(previewText))} tokens
            </span>
          </div>

          {/* Prompt text — always shown, fades when switching parts */}
          <pre
            key={String(previewPart)}
            style={{
              flex: 1,
              fontFamily: mono, fontSize: 11.5, lineHeight: 1.72,
              color: C.codeText,
              padding: '16px 18px', margin: 0,
              overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: 500, textAlign: 'left', direction: 'ltr',
              animation: 'fadeInPrompt 0.35s ease',
            }}
          >{previewText}</pre>
        </div>

      </div>
    </div>
  );
}

/* ─── Token bar ──────────────────────────────────────────────────────── */
function TokenBar({ token, setToken }) {
  const hasToken = !!token;
  return (
    <div style={{
      background: hasToken ? C.greenBg : C.amberBg,
      border: `1px solid ${hasToken ? C.greenBorder : C.amberBorder}`,
      borderRadius: 10,
      padding: '14px 20px',
      marginBottom: 22,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: hasToken ? C.green : C.amber,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: '#fff',
          }} />
        </div>
        <span style={{ fontFamily: font, fontWeight: 700, fontSize: 13, color: hasToken ? C.green : C.amber }}>
          {hasToken ? 'Bearer Token Active' : 'Bearer Token Required'}
        </span>
        {hasToken && (
          <span style={{
            background: C.green, color: '#fff',
            fontFamily: mono, fontSize: 10, fontWeight: 700,
            padding: '2px 8px', borderRadius: 20, letterSpacing: 0.5,
          }}>AUTHENTICATED</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={token}
          onChange={e => setToken(e.target.value)}
          placeholder="Paste your bearer token here, or run Authentication (endpoint 01) to auto-fill"
          style={{
            flex: 1, minWidth: 280,
            padding: '10px 14px',
            border: `1px solid ${hasToken ? C.greenBorder : C.amberBorder}`,
            borderRadius: 7,
            fontFamily: mono, fontSize: 12,
            background: '#fff', color: C.text, outline: 'none',
          }}
        />
        {hasToken && (
          <button onClick={() => setToken('')} style={{
            padding: '10px 16px',
            border: `1px solid ${C.border}`,
            background: '#fff', color: C.textMuted,
            borderRadius: 7, fontFamily: font, fontWeight: 500, fontSize: 13, cursor: 'pointer',
          }}>Clear</button>
        )}
      </div>
      <div style={{ fontFamily: font, fontSize: 11, color: C.textMuted, marginTop: 8 }}>
        Token is stored in <code style={{ fontFamily: mono, fontSize: 11 }}>sessionStorage</code> and is only sent to the ShypV API. It is never shared externally.
      </div>
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────────────── */
export default function ApiDocumentation() {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('');
  const [envKey, setEnvKey] = useState('staging');
  const env = ENVS[envKey];
  const [token, _setToken] = useState(() => sessionStorage.getItem('shypv_doc_token') || '');

  const setToken = v => {
    _setToken(v);
    v ? sessionStorage.setItem('shypv_doc_token', v) : sessionStorage.removeItem('shypv_doc_token');
  };

  useEffect(() => {
    document.title = 'ShypV TMS — API Documentation';
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin: '-15% 0px -65% 0px' }
    );
    ['getting-started', ...SECTIONS.map(s => s.id)].forEach(id => {
      const el = document.getElementById(id); if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.filter(s =>
      [s.title, s.purpose, s.path, s.tag,
        ...(s.requestParams || []).map(p => `${p.name} ${p.desc}`),
        ...(s.responseParams || []).map(p => `${p.name} ${p.desc}`),
      ].join(' ').toLowerCase().includes(q)
    );
  }, [query]);

  const scrollTo = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navIsActive = id => activeId === id;

  return (
    <EnvContext.Provider value={envKey}>
    <>
      <style>{`
        @import url('${FONT_IMPORT}');
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        a { transition: all 0.15s; }
      `}</style>

      <div style={{ fontFamily: font, background: C.bg, minHeight: '100vh', color: C.text }}>

        {/* ── Top nav ── */}
        <nav style={{
          background: '#fff',
          borderBottom: `1px solid ${C.border}`,
          padding: '0 28px',
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img src={shypvLogoDark} alt="ShypV" style={{ height: 32, width: 'auto' }} />
            <div style={{ width: 1, height: 28, background: C.border }} />
            <div>
              <div style={{ fontFamily: font, fontWeight: 700, fontSize: 14, color: C.text, lineHeight: 1.2 }}>TMS Integration API</div>
              <div style={{ fontFamily: font, fontSize: 11, color: C.textMuted }}>Developer Documentation</div>
            </div>
          </div>

          {/* ── Right side controls ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Environment switcher */}
            {Object.entries(ENVS).map(([key, e]) => (
              <button
                key={key}
                onClick={() => setEnvKey(key)}
                style={{
                  fontFamily: font, fontWeight: 600, fontSize: 12,
                  padding: '6px 14px', borderRadius: 20,
                  border: `2px solid ${envKey === key ? e.color : C.border}`,
                  background: envKey === key ? e.bg : '#fff',
                  color: envKey === key ? e.color : C.textMuted,
                  cursor: 'pointer', letterSpacing: 0.3, transition: 'all 0.15s',
                }}
              >{e.label}</button>
            ))}

            <div style={{ width: 1, height: 24, background: C.border }} />

            {/* Partner portal links */}
            <a href="https://business-agent-stage.shypv.com/" target="_blank" rel="noreferrer" style={{
              fontFamily: font, fontSize: 12, fontWeight: 600,
              color: C.amber, textDecoration: 'none',
              border: `1px solid ${C.amberBorder}`, padding: '6px 12px', borderRadius: 7,
              background: C.amberBg, display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.amber, display: 'inline-block' }} />
              Agent (Stage)
            </a>
            <a href="https://business-agent.shypv.com/" target="_blank" rel="noreferrer" style={{
              fontFamily: font, fontSize: 12, fontWeight: 600,
              color: C.green, textDecoration: 'none',
              border: `1px solid ${C.greenBorder}`, padding: '6px 12px', borderRadius: 7,
              background: C.greenBg, display: 'inline-flex', alignItems: 'center', gap: 5,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, display: 'inline-block' }} />
              Agent (Prod)
            </a>

            <div style={{ width: 1, height: 24, background: C.border }} />

            <a href="/" style={{
              fontFamily: font, fontSize: 13, fontWeight: 500,
              color: C.textMuted, textDecoration: 'none',
              border: `1px solid ${C.border}`, padding: '7px 14px', borderRadius: 7,
            }}>Back to TMS</a>
          </div>
        </nav>

        <div style={{ display: 'flex', maxWidth: 1360, margin: '0 auto', padding: '24px 18px', gap: 26 }}>

          {/* ── Sidebar ── */}
          <aside style={{ width: 260, flexShrink: 0 }}>
            <div style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: 16,
              position: 'sticky',
              top: 76,
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search endpoints..."
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    fontFamily: font, fontSize: 13, color: C.text,
                    background: C.bg, outline: 'none',
                  }}
                />
              </div>

              <div style={{ fontFamily: font, fontSize: 9, fontWeight: 700, color: C.textLight, letterSpacing: 2, textTransform: 'uppercase', padding: '4px 8px', marginBottom: 4 }}>
                Overview
              </div>
              <a href="#getting-started" onClick={e => scrollTo(e, 'getting-started')} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 10px', borderRadius: 7,
                fontFamily: font, fontWeight: navIsActive('getting-started') ? 600 : 500, fontSize: 13,
                color: navIsActive('getting-started') ? C.brandDark : C.text,
                textDecoration: 'none',
                background: navIsActive('getting-started') ? C.brandLight : 'transparent',
                borderLeft: `3px solid ${navIsActive('getting-started') ? C.brand : 'transparent'}`,
                marginBottom: 4,
              }}>
                Getting Started
              </a>

              <div style={{ fontFamily: font, fontSize: 9, fontWeight: 700, color: C.textLight, letterSpacing: 2, textTransform: 'uppercase', padding: '10px 8px 4px', marginBottom: 2 }}>
                Endpoints
              </div>
              {SECTIONS.map(s => (
                <a key={s.id} href={`#${s.id}`} onClick={e => scrollTo(e, s.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 10px', borderRadius: 7,
                  fontFamily: font, fontWeight: navIsActive(s.id) ? 600 : 400, fontSize: 13,
                  color: navIsActive(s.id) ? C.brandDark : C.text,
                  textDecoration: 'none',
                  background: navIsActive(s.id) ? C.brandLight : 'transparent',
                  borderLeft: `3px solid ${navIsActive(s.id) ? C.brand : 'transparent'}`,
                  marginBottom: 2,
                  transition: 'all 0.15s',
                }}>
                  <MethodPill method={s.method} small />
                  <span style={{ flex: 1, lineHeight: 1.3 }}>{s.title}</span>
                </a>
              ))}

              <div style={{
                marginTop: 14,
                padding: '12px 14px',
                background: env.bg,
                border: `1px solid ${env.border}`,
                borderRadius: 8,
              }}>
                <div style={{ fontFamily: font, fontWeight: 700, fontSize: 11, color: env.color, marginBottom: 4 }}>{env.label} — Base URL</div>
                <code style={{ fontFamily: mono, fontSize: 11, color: C.text, wordBreak: 'break-all' }}>{env.url}</code>
              </div>

              {/* Partner portal links */}
              <div style={{ marginTop: 10 }}>
                <div style={{ fontFamily: font, fontWeight: 700, fontSize: 10, color: C.textLight, letterSpacing: 1.5, textTransform: 'uppercase', padding: '0 0 6px' }}>Partner Portal</div>
                <a href="https://business-agent-stage.shypv.com/" target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '8px 12px', borderRadius: 7, marginBottom: 6,
                  fontFamily: font, fontSize: 12, fontWeight: 600,
                  color: C.amber, textDecoration: 'none',
                  background: C.amberBg, border: `1px solid ${C.amberBorder}`,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.amber, flexShrink: 0, display: 'inline-block' }} />
                  Agent Portal — Staging
                </a>
                <a href="https://business-agent.shypv.com/" target="_blank" rel="noreferrer" style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '8px 12px', borderRadius: 7,
                  fontFamily: font, fontSize: 12, fontWeight: 600,
                  color: C.green, textDecoration: 'none',
                  background: C.greenBg, border: `1px solid ${C.greenBorder}`,
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, flexShrink: 0, display: 'inline-block' }} />
                  Agent Portal — Production
                </a>
              </div>
            </div>
          </aside>

          {/* ── Main ── */}
          <main style={{ flex: 1, minWidth: 0 }}>

            {/* Hero */}
            <div id="overview" style={{
              background: `linear-gradient(140deg, ${C.navy} 0%, #0d2d4a 100%)`,
              color: '#fff',
              borderRadius: 12,
              padding: '28px 32px',
              marginBottom: 22,
              boxShadow: '0 6px 24px rgba(14,30,60,0.18)',
            }}>
              <div style={{ fontFamily: font, fontWeight: 800, fontSize: 26, marginBottom: 10 }}>ShypV TMS Integration API</div>
              <p style={{ fontFamily: font, fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, margin: '0 0 20px' }}>
                Complete reference for partners integrating with the ShypV Transport Management System. Every endpoint includes interactive "Try it out" functionality — edit the request and execute it live against the selected environment.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                {[
                  [`Base URL (${env.label})`, env.url],
                  ['Authentication', 'Bearer token via /authenticate'],
                  ['Content-Type', 'application/json'],
                  ['Token Validity', 'Approx. 10 hours'],
                ].map(([label, val]) => (
                  <div key={label} style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8, padding: '10px 14px',
                  }}>
                    <div style={{ fontFamily: font, fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4, letterSpacing: 0.5 }}>{label}</div>
                    <code style={{ fontFamily: mono, fontSize: 12, color: '#7dd3fc', wordBreak: 'break-all' }}>{val}</code>
                  </div>
                ))}
              </div>
            </div>

            {/* LLM instruction card — top */}
            <LLMInstructionCard baseUrl={env.url} />

            {/* Onboarding */}
            <Onboarding />

            {/* Token bar */}
            <TokenBar token={token} setToken={setToken} />

            {/* Production warning */}
            {envKey === 'production' && (
              <div style={{
                background: C.redBg,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                padding: '14px 20px',
                marginBottom: 20,
                fontFamily: font,
                fontSize: 13,
                color: C.red,
                lineHeight: 1.6,
              }}>
                <strong>Production environment active.</strong> Requests made via "Try it out" will create, modify or cancel real bookings. Ensure test data is clearly identified before sending requests.
              </div>
            )}

            {/* API cards */}
            {filtered.map(s => (
              <SectionCard key={s.id} section={s} token={token} setToken={setToken} active={navIsActive(s.id)} />
            ))}

            {filtered.length === 0 && (
              <div style={{
                background: '#fff', border: `1px solid ${C.border}`,
                borderRadius: 12, padding: 48, textAlign: 'center',
              }}>
                <div style={{ fontFamily: font, fontWeight: 600, color: C.text, fontSize: 15, marginBottom: 8 }}>No results for "{query}"</div>
                <div style={{ fontFamily: font, fontSize: 13, color: C.textMuted }}>Try a different keyword or clear the search field.</div>
              </div>
            )}

            {/* LLM instruction card — bottom */}
            <LLMInstructionCard baseUrl={env.url} />

            <div style={{ textAlign: 'center', fontFamily: font, fontSize: 12, color: C.textLight, padding: '24px 0 36px' }}>
              ShypV TMS Integration — API Reference
              <span style={{ margin: '0 8px' }}>·</span>
              Technical queries:{' '}
              <a href="mailto:support@shypv.com" style={{ color: C.brand, textDecoration: 'none', fontWeight: 600 }}>support@shypv.com</a>
            </div>
          </main>
        </div>
      </div>
    </>
    </EnvContext.Provider>
  );
}
